import RenderHTML from '../../../../../../../../components/ui-components/RenderHTML'
import { showAsFormattedRupeeAmount, type PlanAmount } from '../../../../../display-values/lumpsumPlanAmount'
import type { LumpsumIdealRecommendation } from '../../../../api/ideal.types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { usePlanBuyAction } from '../../../../../execution/buy/usePlanBuyAction'
import { usePlanSellAction } from '../../../../../execution/sell/usePlanSellAction'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { PlansFundCard, type PlanFundCardCta } from '../../../lumpsum-ui-components/PlansFundCard'
import { planPercentageLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import {
  ALLOCATION_BUY_FUND_CARD_PIE_ICON_SVG
} from '../../allocation/cards/AllocationBuyFundCard'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type IdealFundCardProps = {
  fund: LumpsumIdealRecommendation
  showBottomDivider?: boolean
}

export function IdealFundCard({ fund, showBottomDivider = true }: IdealFundCardProps) {
  const { openIdealPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const { idealData } = useLumpsumPlansContext()
  const { onBuy } = usePlanBuyAction({ transactionCta: idealData?.transactionCta })
  const { onSell } = usePlanSellAction({ transactionCta: idealData?.transactionCta })
  const isin = fund?.isin ?? ''
  const fundName = fund?.fundName ?? ''
  const screenName = PlansScreen.LumpsumIdeal
  const fundRole = PlansFundRole.Ideal
  const sellAmount = fund?.sellAmount
  const buyAmount = fund?.buyAmount

  const openPlanBottomSheet = () => {
    openIdealPlanRecommendationBottomSheet({ isin })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = []
  // 0 / null / undefined → no actionable amount (same as missing)
  if (sellAmount) {
    const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: sellAmount })
    ctas.push({
      id: `${isin}-sell`,
      label: 'Sell',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Sell', fundName, fundIsin: isin })
        onSell({
          isin,
          fundName,
          amount: sellRecommendationFormattedAmount.value,
          currentValue: fund?.currentValue ?? 0
        })
      },
      variant: 'tertiary'
    })
  } else if (buyAmount) {
    const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: buyAmount })
    ctas.push({
      id: `${isin}-buy`,
      label: 'Buy',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Buy', fundName, fundIsin: isin })
        onBuy({
          isin,
          fundName,
          amount: buyRecommendationFormattedAmount.value
        })
      },
      variant: 'tertiary'
    })
  } else {
    ctas.push({
      id: `${isin}-no-action`,
      label: 'No action',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'No action', fundName, fundIsin: isin })
      },
      variant: 'completed'
    })
  }
  ctas.push({
    id: `${isin}-learn-more`,
    label: 'Learn more',
    onClick: () => {
      trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
      openPlanBottomSheet()
    },
    variant: 'tertiary'
  })

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: fund?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: fund?.fundNature ?? '',
        category: fund?.fundCategory ?? '',
        purchaseMode: fund?.purchaseMode ?? ''
      }}
      topRight={<IdealFundCardTrailing fund={fund} />}
      status={{
        rank: fund?.powerRank ?? 0,
        rating: fund?.powerRating ?? ''
      }}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function IdealFundCardTrailing({ fund }: { fund: LumpsumIdealRecommendation }) {
  const { idealData } = useLumpsumPlansContext()
  const buyRecommendationFormattedAmount = resolveIdealBuyAmount(fund, idealData?.idealPortfolioValue ?? 0)
  const isNewFund = (fund?.currentValue ?? 0) <= 0

  return (
    <div className="flex shrink-0 flex-col items-end gap-0">
      <div className="text-[14px] font-[500] leading-[150%] text-[#232323] whitespace-nowrap">
        {buyRecommendationFormattedAmount.label}
      </div>
      <div className="flex items-center justify-end gap-1 py-1 pl-1.5">
        <RenderHTML
          innerHTMLText={ALLOCATION_BUY_FUND_CARD_PIE_ICON_SVG}
          className="size-3 shrink-0 overflow-hidden"
        />
        <div className="text-[13px] font-[400] leading-[140%] text-[#3D3D3D] whitespace-nowrap">
          {planPercentageLabel({ rawPercentage: fund?.idealAllocationPercentage?.absolute ?? 0 })}
        </div>
      </div>
      {isNewFund ? (
        <div className="flex items-center justify-end gap-1 pt-[4px] pl-1.5">
          <RenderHTML
            innerHTMLText={LUMPSUM_IDEAL_FUND_CARD_NEW_FUND_ICON_SVG}
            className="size-3 shrink-0 overflow-hidden"
          />
          <div className="text-[13px] font-[400] leading-[140%] text-[#8E9393] whitespace-nowrap">
            New fund
          </div>
        </div>
      ) : (
        <div className="pl-1.5 text-[13px] pt-[4px] font-[400] leading-[140%] text-[#8E9393] whitespace-nowrap">
          Current fund
        </div>
      )}
    </div>
  )
}

export function resolveIdealBuyAmount(
  fund: LumpsumIdealRecommendation,
  idealPortfolioValue: number
): PlanAmount {
  const idealAbsolutePercentage = fund?.idealAllocationPercentage?.absolute ?? 0
  if (idealPortfolioValue > 0 && idealAbsolutePercentage > 0) {
    return showAsFormattedRupeeAmount({
      rawAmount: (idealPortfolioValue * idealAbsolutePercentage) / 100
    })
  }
  return showAsFormattedRupeeAmount({ rawAmount: fund?.currentValue ?? 0 })
}

export const LUMPSUM_IDEAL_FUND_CARD_NEW_FUND_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_94485_119550)">
    <path d="M8.2539 4.66162C8.33415 6.2691 9.6059 7.54085 11.2134 7.6211C11.3033 7.62327 11.375 7.69675 11.375 7.78665V7.99548C11.375 8.20508 11.2043 8.3726 10.9961 8.3973C9.56431 8.56719 8.44219 9.68932 8.2723 11.1211C8.2476 11.3293 8.08008 11.5 7.87047 11.5H7.66165C7.57175 11.5 7.49827 11.4283 7.4961 11.3384C7.41947 9.80405 6.25735 8.5757 4.75387 8.3973C4.54572 8.3726 4.375 8.20508 4.375 7.99548V7.78667C4.375 7.69676 4.44674 7.62327 4.53662 7.6211C6.07095 7.54448 7.2993 6.38235 7.4777 4.87887C7.5024 4.67072 7.66992 4.5 7.87952 4.5H8.08833C8.17824 4.5 8.25173 4.57174 8.2539 4.66162ZM3.62793 0.859865C3.68246 1.95332 4.54668 2.81754 5.64015 2.87207C5.70129 2.8737 5.75 2.92373 5.75 2.98489V3.245C5.75 3.45487 5.57843 3.62145 5.37176 3.65792C4.49581 3.81252 3.81252 4.49581 3.65792 5.37176C3.62144 5.57843 3.45487 5.75 3.245 5.75H2.98489C2.92373 5.75 2.8737 5.70129 2.87207 5.64015C2.82209 4.63784 2.09179 3.82798 1.12823 3.65792C0.921555 3.62144 0.75 3.45487 0.75 3.245V2.9849C0.75 2.92374 0.79872 2.8737 0.859865 2.87207C1.86216 2.82209 2.67202 2.09179 2.84208 1.12823C2.87856 0.921555 3.04513 0.75 3.255 0.75H3.5151C3.57626 0.75 3.6263 0.79872 3.62793 0.859865Z" fill="#8E9393"/>
  </g>
  <defs>
    <clipPath id="clip0_94485_119550">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`
